import { useEffect, type MutableRefObject } from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { toast } from "sonner";
import type { Point } from "@/lib/geometry";

interface UseMeasurementOverlayGlobalShortcutsArgs {
  interactionRef: MutableRefObject<unknown | null>;
  drawingDraftRef: MutableRefObject<unknown | null>;
  latestDraftRef: MutableRefObject<unknown | null>;
  isSpacePressedRef: MutableRefObject<boolean>;
  lastPointerPagePointRef: MutableRefObject<Point | null>;
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  activeMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  canPasteMeasurement: boolean;
  contextMenuOpen: boolean;
  clearSelection: () => void;
  cancelDrawingSession: () => void;
  setContextMenuOpen: (open: boolean) => void;
  onCopyMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onCopyMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => void;
  onPasteMeasurement?: (point?: Point) => Promise<void> | void;
  onDuplicateMeasurement?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    point?: Point
  ) => Promise<void> | void;
  onDuplicateMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    point?: Point
  ) => Promise<void> | void;
}

export function useMeasurementOverlayGlobalShortcuts({
  interactionRef,
  drawingDraftRef,
  latestDraftRef,
  isSpacePressedRef,
  lastPointerPagePointRef,
  selectedMeasurementIds,
  activeMeasurementId,
  canPasteMeasurement,
  contextMenuOpen,
  clearSelection,
  cancelDrawingSession,
  setContextMenuOpen,
  onCopyMeasurement,
  onCopyMeasurements,
  onPasteMeasurement,
  onDuplicateMeasurement,
  onDuplicateMeasurements,
}: UseMeasurementOverlayGlobalShortcutsArgs) {
  useEffect(() => {
    const handleModifierShortcut = (
      event: KeyboardEvent,
      isEditableTarget: boolean
    ): boolean => {
      if (!(event.metaKey || event.ctrlKey) || isEditableTarget) {
        return false;
      }

      const pointerPoint = lastPointerPagePointRef.current ?? undefined;
      const key = event.key.toLowerCase();
      const runShortcutAction = (
        action: () => Promise<void> | void,
        fallbackMessage: string
      ) => {
        try {
          const result = action();
          Promise.resolve(result).catch((error) => {
            console.error(error);
            toast.error(fallbackMessage);
          });
        } catch (error) {
          console.error(error);
          toast.error(fallbackMessage);
        }
      };

      if (key === "c") {
        const canCopyMany = selectedMeasurementIds.length > 1 && !!onCopyMeasurements;
        const canCopyOne = !!activeMeasurementId && !!onCopyMeasurement;
        if (!canCopyMany && !canCopyOne) {
          return false;
        }

        event.preventDefault();
        if (canCopyMany) {
          onCopyMeasurements(selectedMeasurementIds);
        } else if (activeMeasurementId && onCopyMeasurement) {
          onCopyMeasurement(activeMeasurementId);
        }
        setContextMenuOpen(false);
        return true;
      }

      if (key === "v" && canPasteMeasurement && onPasteMeasurement) {
        event.preventDefault();
        runShortcutAction(
          () => onPasteMeasurement(pointerPoint),
          "Failed to paste measurement"
        );
        setContextMenuOpen(false);
        return true;
      }

      if (key === "d") {
        const canDuplicateMany =
          selectedMeasurementIds.length > 1 && !!onDuplicateMeasurements;
        const canDuplicateOne = !!activeMeasurementId && !!onDuplicateMeasurement;
        if (!canDuplicateMany && !canDuplicateOne) {
          return false;
        }

        event.preventDefault();
        if (canDuplicateMany) {
          runShortcutAction(
            () => onDuplicateMeasurements(selectedMeasurementIds, pointerPoint),
            "Failed to duplicate measurements"
          );
        } else if (activeMeasurementId && onDuplicateMeasurement) {
          runShortcutAction(
            () => onDuplicateMeasurement(activeMeasurementId, pointerPoint),
            "Failed to duplicate measurement"
          );
        }
        setContextMenuOpen(false);
        return true;
      }

      return false;
    };

    const handleEscapeShortcut = (
      event: KeyboardEvent,
      isEditableTarget: boolean
    ): boolean => {
      if (event.key !== "Escape") {
        return false;
      }
      if (isEditableTarget) {
        return false;
      }

      const hasDraftState =
        !!interactionRef.current || !!drawingDraftRef.current || !!latestDraftRef.current;
      const hasSelection = selectedMeasurementIds.length > 0;
      if (!hasDraftState && !hasSelection && !contextMenuOpen) {
        return false;
      }

      event.preventDefault();
      if (hasDraftState) {
        cancelDrawingSession();
      }
      if (hasSelection) {
        clearSelection();
      }
      setContextMenuOpen(false);
      return true;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isEditableTarget = Boolean(
        target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable)
      );

      if (event.key === " " && !isEditableTarget) {
        isSpacePressedRef.current = true;
      }

      if (handleModifierShortcut(event, isEditableTarget)) {
        return;
      }

      void handleEscapeShortcut(event, isEditableTarget);
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === " ") {
        isSpacePressedRef.current = false;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [
    activeMeasurementId,
    canPasteMeasurement,
    cancelDrawingSession,
    clearSelection,
    contextMenuOpen,
    drawingDraftRef,
    interactionRef,
    isSpacePressedRef,
    lastPointerPagePointRef,
    latestDraftRef,
    onCopyMeasurement,
    onCopyMeasurements,
    onDuplicateMeasurement,
    onDuplicateMeasurements,
    onPasteMeasurement,
    selectedMeasurementIds,
    setContextMenuOpen,
  ]);

  useEffect(() => {
    if (!contextMenuOpen) {
      return;
    }

    const closeContextMenu = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        setContextMenuOpen(false);
        return;
      }
      if (target.closest("[data-measurement-context-menu='true']")) {
        return;
      }
      setContextMenuOpen(false);
    };
    window.addEventListener("mousedown", closeContextMenu);
    return () => window.removeEventListener("mousedown", closeContextMenu);
  }, [contextMenuOpen, setContextMenuOpen]);
}
